// ============================================================================
// Kit canônico de botões (regra FC7 — .claude/rules/fc7-design-system.md).
//
// Semântica de cor RATIFICADA (2026-07-02; azul clareado em 2026-07-03):
//   PrimaryButton (azul #4098ef)   → ação que ESCREVE no banco (salvar, criar,
//                                    confirmar, importar — POST/PUT)
//   CtaButton (escuro #1f1f18)     → ação consultiva/navegação (pesquisar,
//                                    exportar, abrir mapa, redirecionar)
//   DangerButton (vermelho)        → DELETE/destrutivo, sempre
//   GhostButton                    → secundário (cancelar, voltar, limpar)
//   SmallButton ($intent)          → ação de linha de tabela/painel (32px)
//   IconButton ($intent)           → ícone isolado
//   TabButton ($active)            → navegação por abas
//   OptionButton                   → linha de dropdown/autocomplete
// ============================================================================
import styled, { css } from 'styled-components';

// Base compartilhada — geometria canônica 44px / radius 11 (origem:
// pages/Routing/RoutingItemNew/components/manual/styles.js)
const buttonBase = css`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   height: 44px;
   padding: 0 16px;
   border-radius: 11px;
   font-size: 14px;
   font-weight: 600;
   font-family: inherit;
   cursor: pointer;
   appearance: none;
   -webkit-appearance: none;
   transition:
      background 0.13s ease,
      color 0.13s ease,
      border-color 0.13s ease;

   &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

// Azul #4098ef — ESCREVE no banco (salvar/confirmar/criar/importar)
export const PrimaryButton = styled.button`
   ${buttonBase}
   border: 0;
   padding: 0 18px;
   background: ${({ theme }) => theme.colors.actionWrite};
   color: #fff;
   font-weight: 700;

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.actionWriteHover};
   }
`;

// CTA escuro #1f1f18 — consulta/navegação (pesquisar/exportar/abrir mapa)
export const CtaButton = styled.button`
   ${buttonBase}
   border: 0;
   padding: 0 18px;
   background: ${({ theme }) => theme.colors.ctaDark};
   color: #fff;
   font-weight: 700;

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.ctaDarkHover};
   }
`;

// Vermelho — destrutivo (DELETE). Sólido: é a confirmação da destruição.
export const DangerButton = styled.button`
   ${buttonBase}
   border: 0;
   padding: 0 18px;
   background: ${({ theme }) => theme.colors.danger};
   color: #fff;
   font-weight: 700;

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.dangerHover};
   }
`;

// Secundário — cancelar/voltar/limpar
export const GhostButton = styled.button`
   ${buttonBase}
   border: 1px solid ${({ theme }) => theme.colors.border};
   background: transparent;
   color: ${({ theme }) => theme.colors.textPrimary};

   &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.04);
   }
`;

// Cor de texto por intenção (SmallButton neutro/link e IconButton no hover)
const intentColor = ({ $intent, theme }) => {
   if ($intent === 'write') return theme.colors.actionWrite;
   if ($intent === 'cta') return theme.colors.ctaDark;
   if ($intent === 'danger') return theme.colors.danger;
   if ($intent === 'link') return theme.colors.link;
   return theme.colors.textPrimary; // neutral (default)
};

// Ação de linha de tabela/painel — 32px.
// $intent: 'neutral' (ghost, default) | 'write' | 'cta' | 'danger' | 'link'
export const SmallButton = styled.button`
   ${buttonBase}
   height: 32px;
   padding: 0 11px;
   border-radius: 8px;
   font-size: 12.5px;
   gap: 6px;

   border: 1px solid
      ${({ $intent, theme }) => {
         if ($intent === 'link' || $intent === 'write' || $intent === 'cta')
            return 'transparent';
         if ($intent === 'danger') return theme.colors.dangerBorder;
         return theme.colors.border;
      }};
   background: ${({ $intent, theme }) => {
      if ($intent === 'write') return theme.colors.actionWrite;
      if ($intent === 'cta') return theme.colors.ctaDark;
      if ($intent === 'danger') return theme.colors.dangerSoft;
      return 'transparent';
   }};
   color: ${({ $intent, theme }) =>
      $intent === 'write' || $intent === 'cta'
         ? '#fff'
         : intentColor({ $intent, theme })};

   &:hover:not(:disabled) {
      ${({ $intent, theme }) => {
         if ($intent === 'write')
            return `background: ${theme.colors.actionWriteHover};`;
         if ($intent === 'cta')
            return `background: ${theme.colors.ctaDarkHover};`;
         if ($intent === 'danger')
            return 'background: rgba(171, 32, 32, 0.16);';
         if ($intent === 'link') return 'text-decoration: underline;';
         return 'background: rgba(0, 0, 0, 0.04);';
      }}
   }
`;

// Botão de ícone isolado — 36x36. $intent="danger" para lixeiras.
export const IconButton = styled.button`
   ${buttonBase}
   height: 36px;
   width: 36px;
   padding: 0;
   border-radius: 8px;
   border: 0;
   background: transparent;
   color: ${({ theme }) => theme.colors.textMuted};

   &:hover:not(:disabled) {
      background: ${({ $intent, theme }) =>
         $intent === 'danger' ? theme.colors.dangerSoft : 'rgba(0, 0, 0, 0.06)'};
      color: ${intentColor};
   }
`;

// Aba — consolida os tabs #0288d1 do Billing no azul canônico
export const TabButton = styled.button`
   appearance: none;
   border: 0;
   background: none;
   cursor: pointer;
   font-family: inherit;
   padding: 10px 14px;
   font-size: 13.5px;
   font-weight: 600;
   color: ${({ $active, theme }) =>
      $active ? theme.colors.actionWrite : theme.colors.textMuted};
   border-bottom: 3px solid
      ${({ $active, theme }) =>
         $active ? theme.colors.actionWrite : 'transparent'};
   margin-bottom: -1px;
   transition:
      color 0.13s ease,
      border-color 0.13s ease;

   &:hover:not(:disabled) {
      color: ${({ theme }) => theme.colors.actionWrite};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
`;

// ---------------------------------------------------------------------------
// Variantes para o HEADER ESCURO da página (ratificadas em 2026-09-10, na
// refatoração visual de Transferências — decisão D1). Mesma semântica G.1,
// mudando só a superfície: sobre o gradiente escuro do shell, o par
// consultivo/destrutivo vira outline claro (botão branco com opacity fica
// ilegível, ~1,8:1). O :disabled é redefinido porque o token global
// (opacity .55) foi pensado para superfície clara — aqui o estado desabilitado
// mantém contraste legível, o que importa para a barreira de PDV
// (GuardedAction impõe disabled com o motivo no wrapper).
// ---------------------------------------------------------------------------

// ⚠️ Irmã da armadilha G.4.1, versão <a>: estas variantes são usadas com
// `as={Link}` (navegação real, G.3), e o `a:hover` do GlobalStyles (elemento
// + pseudo-classe = 0,0,1,1) vence a classe do styled-component (0,0,1,0).
// Por isso TODO hover aqui redeclara `color` e `text-decoration`, não só
// `background` — sem isso o texto pisca no azul de link sublinhado do legado.
const onDarkBase = css`
   ${buttonBase}
   height: 36px;
   padding: 0 14px;
   border-radius: 10px;
   font-size: 13px;
   background: transparent;
   text-decoration: none;
`;

// Consultivo/navegação no header escuro (equivale ao CtaButton/GhostButton).
export const GhostOnDarkButton = styled.button`
   ${onDarkBase}
   border: 1px solid ${({ theme }) => theme.colors.controlBorderOnAppBackground};
   color: ${({ theme }) => theme.colors.textOnAppBackground};

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.controlHoverOnAppBackground};
      color: ${({ theme }) => theme.colors.textOnAppBackground};
      text-decoration: none;
   }
   &:disabled {
      opacity: 1;
      color: rgba(255, 255, 255, 0.45);
      border-color: rgba(255, 255, 255, 0.16);
      background: transparent;
   }
`;

// CTA de destaque no header escuro (equivale ao CtaButton): sólido BRANCO com
// texto escuro — o CTA #1f1f18 desaparece sobre o gradiente, e o azul sólido é
// reservado à escrita (G.1). Uso: ação de navegação principal da tela.
export const CtaOnDarkButton = styled.button`
   ${onDarkBase}
   border: 0;
   background: #fff;
   color: ${({ theme }) => theme.colors.textPrimary};
   font-weight: 700;

   &:hover:not(:disabled) {
      background: #e9eef5;
      color: ${({ theme }) => theme.colors.textPrimary};
      text-decoration: none;
   }
   &:disabled {
      opacity: 1;
      background: rgba(255, 255, 255, 0.35);
      color: rgba(0, 0, 0, 0.45);
   }
`;

// Escrita no banco em SUPERFÍCIE ESCURA (equivale ao PrimaryButton). Nasceu
// em 2026-09-15, na barra de salvamento fixa do redesenho do RoleShow: sobre
// #2e2e2e o azul sólido #4098ef perde destaque, e o design ratificou o azul
// suave #96c4f0 com texto marinho — mesma semântica G.1 (só escrita real),
// outra superfície.
export const PrimaryOnDarkButton = styled.button`
   ${onDarkBase}
   border: 0;
   background: ${({ theme }) => theme.colors.primarySoft};
   color: ${({ theme }) => theme.colors.onPrimarySoft};
   font-weight: 800;

   &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primarySoftHover};
      color: ${({ theme }) => theme.colors.onPrimarySoft};
      text-decoration: none;
   }
   &:disabled {
      opacity: 1;
      background: rgba(150, 196, 240, 0.35);
      color: rgba(18, 49, 79, 0.55);
   }
`;

// Destrutivo no header escuro (equivale ao DangerButton em outline claro).
export const DangerOnDarkButton = styled.button`
   ${onDarkBase}
   border: 1px solid rgba(255, 138, 138, 0.5);
   color: #ffb4b4;

   &:hover:not(:disabled) {
      background: rgba(220, 38, 38, 0.18);
      color: #ffb4b4;
      text-decoration: none;
   }
   &:disabled {
      opacity: 1;
      color: rgba(255, 180, 180, 0.45);
      border-color: rgba(255, 138, 138, 0.2);
      background: transparent;
   }
`;

// Linha de dropdown/autocomplete (Row dos *NameSearch). $active = item
// destacado (navegação por teclado / seleção corrente).
export const OptionButton = styled.button`
   appearance: none;
   display: block;
   width: 100%;
   text-align: left;
   padding: 10px 12px;
   border: 0;
   background: ${({ $active }) =>
      $active ? 'rgba(64, 152, 239, 0.14)' : 'transparent'};
   color: ${({ theme }) => theme.colors.textPrimary};
   font-size: 0.85rem;
   font-weight: ${({ $active }) => ($active ? 600 : 400)};
   font-family: inherit;
   cursor: pointer;

   &:hover,
   &:focus-visible {
      outline: none;
      background: rgba(64, 152, 239, 0.14);
   }
`;
